import React, { useState, useMemo } from "react";
import { Plus } from "lucide-react";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { PortalDsField, PortalDsSelect } from "src/components/portal-design-system/portal-ds-field";
import InternalReferralsTable from "../components/internal-referrals-table";
import InternalReferralDrawer from "../components/internal-referral-drawer";
import type { InternalReferral } from "../types/commissions.types";
import {
  useGetInternalReferralsQuery,
  useCreateInternalReferralMutation,
  useUpdateInternalReferralMutation,
  useGetCommissionReportsQuery,
} from "../endpoints/commissions.endpoints";
import { useGetCommissionUsersQuery } from "../endpoints/commissions.endpoints";
import { useGetCompaniesQuery } from "src/redux/billing/billing.endpoints";
import {
  monthYearToQuarterPeriod,
  useReportCommissionViewPeriod,
} from "../commission-view-period-context";

const PERIOD_TYPE_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const label = new Date(2000, i, 1).toLocaleString("default", { month: "long" });
  return { value: String(month), label };
});

function getQuartersForPeriod(): { label: string; year: number; quarter: number }[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);
  const quarters: { label: string; year: number; quarter: number }[] = [];
  for (let i = -2; i <= 1; i++) {
    const total = currentYear * 4 + currentQuarter + i;
    const year = Math.floor((total - 1) / 4);
    const quarter = ((total - 1) % 4) + 1;
    quarters.push({ label: `Q${quarter} ${year}`, year, quarter });
  }
  return quarters;
}

function getYearsForPeriod(): number[] {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1];
}

const QUARTERS = getQuartersForPeriod();
const YEARS = getYearsForPeriod();

const InternalReferralsView: React.FC = () => {
  const { data: referrals = [] } = useGetInternalReferralsQuery();
  const [createReferral] = useCreateInternalReferralMutation();
  const [updateReferral] = useUpdateInternalReferralMutation();
  const { data: users = [] } = useGetCommissionUsersQuery();
  const { data: companies = [] } = useGetCompaniesQuery();

  const employeeOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.name })),
    [users]
  );
  const clientOptions = useMemo(
    () =>
      companies.map((c) => ({
        value: c.ext_customer_id ?? c.ext_company_id ?? c.id,
        label: c.company_name ?? "",
      })),
    [companies]
  );

  const [periodType, setPeriodType] = useState<"month" | "quarter">("quarter");
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const q = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${q} ${y}`;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<InternalReferral | null>(null);
  const [isNewReferral, setIsNewReferral] = useState(false);

  const periodLabel = useMemo(() => {
    if (periodType === "month") {
      const monthIndex = Number(selectedMonth) - 1;
      const monthName = new Date(2000, monthIndex, 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${selectedYear}`;
    }
    return selectedQuarter;
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const viewPeriodForLoad = useMemo(() => {
    if (periodType === "quarter") {
      const q = QUARTERS.find((x) => x.label === selectedQuarter);
      if (!q) return null;
      return { quarter: `Q${q.quarter}`, year: q.year };
    }
    const month = Number(selectedMonth);
    const year = Number(selectedYear);
    if (!month || !year) return null;
    return monthYearToQuarterPeriod(month, year);
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  useReportCommissionViewPeriod(viewPeriodForLoad);

  const reportParamsList = useMemo(() => {
    if (periodType === "month") {
      return [{ type: "internal_referral" as const, month: Number(selectedMonth), year: Number(selectedYear) }];
    }
    const q = QUARTERS.find((x) => x.label === selectedQuarter);
    if (!q) return [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const quarterMonths = [
      { type: "internal_referral" as const, month: (q.quarter - 1) * 3 + 1, year: q.year },
      { type: "internal_referral" as const, month: (q.quarter - 1) * 3 + 2, year: q.year },
      { type: "internal_referral" as const, month: (q.quarter - 1) * 3 + 3, year: q.year },
    ];
    return quarterMonths.filter(
      (p) => p.year < currentYear || (p.year === currentYear && p.month < currentMonth)
    );
  }, [periodType, selectedMonth, selectedYear, selectedQuarter]);

  const reportParams0 = reportParamsList[0];
  const reportParams1 = reportParamsList[1];
  const reportParams2 = reportParamsList[2];
  const skipReports = reportParamsList.length === 0;
  const { data: reports1 = [] } = useGetCommissionReportsQuery(reportParams0 ?? { type: "internal_referral" }, { skip: skipReports });
  const { data: reports2 = [] } = useGetCommissionReportsQuery(reportParams1 ?? reportParams0 ?? { type: "internal_referral" }, { skip: skipReports || reportParamsList.length < 2 });
  const { data: reports3 = [] } = useGetCommissionReportsQuery(reportParams2 ?? reportParams0 ?? { type: "internal_referral" }, { skip: skipReports || reportParamsList.length < 3 });

  const referralIdToEstimatedCommission = useMemo(() => {
    const all = [reports1, reports2, reports3].slice(0, reportParamsList.length).flat();
    const byReferral: Record<string, number> = {};
    for (const r of all) {
      const id = r.internalReferralId ?? (r.details as { internal_referral_id?: string } | undefined)?.internal_referral_id ?? "";
      if (!id) continue;
      byReferral[id] = (byReferral[id] ?? 0) + (r.commission ?? 0);
    }
    return byReferral;
  }, [reports1, reports2, reports3, reportParamsList.length]);

  const handleRowClick = (referral: InternalReferral) => {
    setSelectedReferral(referral);
    setIsNewReferral(false);
    setDrawerOpen(true);
  };

  const handleAddReferral = () => {
    setSelectedReferral(null);
    setIsNewReferral(true);
    setDrawerOpen(true);
  };

  const handleSaveReferral = async (payload: Partial<InternalReferral> & { id?: string }) => {
    try {
      if (payload.id) {
        await updateReferral({
          id: payload.id,
          referringEmployeeId: payload.referringEmployeeId,
          clientId: payload.clientId,
          clientName: payload.clientName,
          startDate: payload.startDate,
          tier: payload.tier,
        }).unwrap();
      } else if (
        payload.referringEmployeeId
        && payload.clientId
        && payload.clientName
        && payload.startDate
        && payload.tier
      ) {
        await createReferral({
          referringEmployeeId: payload.referringEmployeeId,
          clientId: payload.clientId,
          clientName: payload.clientName,
          startDate: payload.startDate,
          tier: payload.tier,
        }).unwrap();
      }
      setDrawerOpen(false);
    } catch {
      // Error could be surfaced via toast
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <PortalDsField
            label="View by"
            htmlFor="internal-period-type"
            filter
            className="w-[8.5rem]"
          >
            <PortalDsSelect
              id="internal-period-type"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as "month" | "quarter")}
            >
              {PERIOD_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </PortalDsSelect>
          </PortalDsField>
          {periodType === "month" ? (
            <>
              <PortalDsField
                label="Month"
                htmlFor="internal-month"
                filter
                className="w-[9.5rem]"
              >
                <PortalDsSelect
                  id="internal-month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {MONTH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
              <PortalDsField
                label="Year"
                htmlFor="internal-year"
                filter
                className="w-[7rem]"
              >
                <PortalDsSelect
                  id="internal-year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {YEARS.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
            </>
          ) : (
            <PortalDsField
              label="Quarter"
              htmlFor="internal-quarter"
              filter
              className="w-[8.5rem]"
            >
              <PortalDsSelect
                id="internal-quarter"
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
              >
                {QUARTERS.map((q) => (
                  <option key={q.label} value={q.label}>
                    {q.label}
                  </option>
                ))}
              </PortalDsSelect>
            </PortalDsField>
          )}
        </div>
        <PortalDsButton type="button" variant="secondary" size="sm" onClick={handleAddReferral}>
          <Plus className="h-4 w-4" />
          Add referral
        </PortalDsButton>
      </div>
      <InternalReferralsTable
        referrals={referrals}
        quarterLabel={periodLabel}
        onRowClick={handleRowClick}
        referralIdToEstimatedCommission={referralIdToEstimatedCommission}
      />
      <InternalReferralDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        referral={selectedReferral}
        isNew={isNewReferral}
        onSave={handleSaveReferral}
        employeeOptions={employeeOptions}
        clientOptions={clientOptions}
      />
    </div>
  );
};

export default InternalReferralsView;
